// ============================================================================
// One service line on the AM entry screen. Run B Phase 2, 2026-08-18.
//
// Each line carries its own scope, size, sub, dates and rates, because that is
// what a project actually is — "carpet clean Tuesday with Rodrigo, strip and wax
// Saturday with Clean All" is one job to the client and two to finance.
//
// Both dates are kept, always. Recording when the work actually happened never
// touches what was first scheduled: they are separate columns, separate inputs,
// and timing_state is derived by the database from the pair. Nothing here sets
// timing_state — it is rendered, never chosen.
// ============================================================================
import { Trash2, MinusCircle } from 'lucide-react';
import { ProjectServiceTypeSelect } from '../service-type-select';
import {
  chargebackBlocker,
  clearsMarginFloor,
  isChargeback,
  marginPct,
  num,
  MARGIN_FLOOR_PCT,
  type EntryLine,
} from '../lib/entry-derive';
// A typed $0 is a CLAIM and gets asked about; a blank is an unfinished form and
// does not. That distinction — and it is the whole rule — lives in lib/zero-prompt.
import { shouldAskAboutZero, type ZeroSide } from '../lib/zero-prompt';
import { SHOW_SUB_PAY_IN_PRIOR_WORK, usePriorWorkHere, useProjectSubGroups } from './use-entry-data';
import { SmartSelect } from '../../../components/smart-select';
import { isProviderSelectable } from '../lib/sub-picker';

/**
 * What the list is, in the AM's terms. ⛔ The old tooltip claimed the list was
 * "Ranked by who can do this work, then by who has worked this facility" — true
 * of the flat <select> it described and FALSE of a searchable picker. A tooltip
 * that describes behaviour the screen no longer has is worse than none.
 */
const SUB_PICKER_TOOLTIP =
  'Subs who have done project work at this building come first, then the subs ' +
  'who clean it at night, then subs doing project work elsewhere in this market. ' +
  'Type to search, or use “Show all subs” for everyone. Inactive subs are not ' +
  'offered on a new line, but a line that already names one still shows it.';

const INPUT_CLASS =
  'block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text';

const money = (n: number | null) =>
  n === null ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

interface Props {
  line: EntryLine;
  index: number;
  total: number;
  facilityId: string | null;
  /**
   * The project being edited, so "Has done project work here" does not list
   * this project's own lines. A new project has none yet — null is fine.
   */
  projectId?: string | null;
  // 2026-09-02 — `providers` and `history` are gone. Both existed to build a
  // ranked flat list of 75 Active subs; SmartSelect fetches and scopes its own
  // rows, so passing a second list down would be two sources for one dropdown.
  otherServiceCode: string;
  onChange: (patch: Partial<EntryLine>) => void;
  onRemove: () => void;
  /**
   * A typed $0 left the box. The CARD does not decide what that means — the
   * answer changes the PROJECT's classification, not this line, so the screen
   * that owns the project owns the dialog. Optional: a surface that has no
   * classification to change simply does not pass it.
   *
   * ⛔ On blur, never on change. Firing per keystroke would interrupt someone
   * halfway through typing "0.50", which is how people learn to dismiss dialogs
   * without reading them.
   */
  onZeroTyped?: (side: ZeroSide, entered: boolean) => void;
}

export function ServiceLineCard({
  line,
  index,
  total,
  facilityId,
  projectId = null,
  otherServiceCode,
  onChange,
  onRemove,
  onZeroTyped,
}: Props) {
  const isOther = line.service_code === otherServiceCode;
  const prior = usePriorWorkHere(facilityId, line.service_code || null);
  const chargeback = isChargeback(line);
  // ⛔ Called before the chargeback early-return so the hook order never
  // changes between renders. The chargeback picker does not use it, so a
  // chargeback card fetches nothing.
  const subGroups = useProjectSubGroups(chargeback ? null : facilityId, projectId);
  const blocker = chargebackBlocker(line);

  // A chargeback is a different kind of thing and gets a different, smaller
  // card: who, how much, why. No service picker (it is not a service), no dates
  // (it is not a visit), no client rate (the client is never billed for it).
  // The amount is typed as a positive number and stored negative, because
  // "charge Alba 50" is what the AM means and "-50" is only how it is filed.
  if (chargeback) {
    const typed = num(line.sub_rate);
    const shown = typed === null ? '' : String(Math.abs(typed));
    return (
      <div className="rounded-lg border border-st-danger-border bg-st-danger-bg p-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-st-danger-fg">
            <MinusCircle className="h-3.5 w-3.5" aria-hidden="true" />
            Charge back to a sub
          </span>
          <button
            type="button"
            onClick={onRemove}
            className="inline-flex items-center gap-1 text-[11px] text-tcs-text-secondary hover:text-st-danger-fg"
          >
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-[11px] text-tcs-text-secondary">Which sub is being charged</span>
            {/* 2026-09-02 — the app's own searchable picker, not a second one.
                A chargeback is money recovered from a sub who may well have
                been retired since, so the name has to resolve even when the sub
                can no longer be newly assigned. */}
            <SmartSelect
              entity="service_provider"
              value={line.assigned_partner_id || null}
              onChange={(id) => onChange({ assigned_partner_id: id || '' })}
              isSelectable={isProviderSelectable}
              entityLabel="sub"
              placeholder="Search subs…"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] text-tcs-text-secondary">Take off their pay (USD)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={shown}
              onChange={(e) => {
                const v = e.target.value.trim();
                const n = Number(v);
                onChange({ sub_rate: v === '' || !Number.isFinite(n) ? '' : String(-Math.abs(n)) });
              }}
              className={INPUT_CLASS}
            />
          </label>
        </div>

        <label className="mt-3 block">
          <span className="mb-1 block text-[11px] text-tcs-text-secondary">
            Why — required
          </span>
          <input
            type="text"
            value={line.scope_text}
            onChange={(e) => onChange({ scope_text: e.target.value })}
            placeholder="Her crew caused the damage. Agreed 50/50 with Alba."
            className={INPUT_CLASS}
          />
        </label>

        <p className="mt-2 text-[11px] text-tcs-text-muted">
          This reduces what that sub is paid. It does not change what the client is billed.
        </p>
        {blocker && (
          <p className="mt-2 rounded-md bg-st-warn-bg px-3 py-2 text-[11.5px] text-st-warn-fg">
            Not saved yet — {blocker}.
          </p>
        )}
      </div>
    );
  }

  const pct = marginPct(num(line.client_rate), num(line.sub_rate));
  const clears = clearsMarginFloor(pct);

  return (
    <div className="rounded-lg border border-tcs-border bg-tcs-bg p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
          Service {index + 1}
          {total > 1 ? ` of ${total}` : ''}
        </span>
        {total > 1 && (
          <button
            type="button"
            onClick={onRemove}
            className="inline-flex items-center gap-1 text-[11px] text-tcs-text-secondary hover:text-st-danger-fg"
          >
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </button>
        )}
      </div>

      <ProjectServiceTypeSelect
        divisionId={line.division_id}
        onDivisionChange={(v) => onChange({ division_id: v })}
        value={line.service_code}
        onChange={(v) => onChange({ service_code: v })}
        scopeText={line.scope_text}
        onScopeTextChange={(v) => onChange({ scope_text: v })}
        selectClassName={INPUT_CLASS}
        inputClassName={INPUT_CLASS}
      />

      {/* Prior work at this facility for this service — what was billed, and
          what the sub was paid. Shown as soon as a service is chosen. */}
      {line.service_code && !isOther && facilityId && prior.hasFetched && (
        <div className="mt-2 rounded-md bg-tcs-field px-3 py-2">
          <p className="text-[10.5px] font-medium uppercase tracking-wide text-tcs-text-muted">
            {prior.rows.length > 0
              ? `This service here · ${prior.rows.length} prior job${prior.rows.length > 1 ? 's' : ''}`
              : 'Nothing here before'}
          </p>
          {prior.rows.length === 0 && (
            <p className="mt-1 text-[11.5px] text-tcs-text-secondary">
              No prior record of this service at this facility.
            </p>
          )}
          {prior.rows.map((r) => (
            <p key={r.service_line_id} className="mt-1 text-[11.5px] text-tcs-text-secondary">
              <span className="text-tcs-text">{r.date ?? 'undated'}</span>
              {r.provider_name ? ` — ${r.provider_name}` : ' — no sub recorded'}
              {' · billed '}
              {money(r.client_rate)}
              {SHOW_SUB_PAY_IN_PRIOR_WORK ? ` · paid ${money(r.sub_rate)}` : ''}
            </p>
          ))}
        </div>
      )}

      {/* scope_text is ONE column. When the escape hatch is chosen the picker
          above already owns it (and requires it), so showing a second input
          bound to the same value would be two boxes that mirror each other. */}
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {!isOther && (
          <label className="block sm:col-span-2">
            <span className="mb-1 block text-[11px] text-tcs-text-secondary">Area or detail</span>
            <input
              type="text"
              value={line.scope_text}
              onChange={(e) => onChange({ scope_text: e.target.value })}
              placeholder="Only suites 200 and 205"
              className={INPUT_CLASS}
            />
          </label>
        )}
        <label className="block">
          <span className="mb-1 block text-[11px] text-tcs-text-secondary">Square feet</span>
          <input
            type="number"
            min="0"
            value={line.scope_qty}
            onChange={(e) => onChange({ scope_qty: e.target.value })}
            placeholder="1000"
            className={INPUT_CLASS}
          />
        </label>
      </div>

      <label className="mt-3 block">
        <span className="mb-1 block text-[11px] text-tcs-text-secondary">
          Subcontractor{' '}
          <span title={SUB_PICKER_TOOLTIP} className="cursor-help underline decoration-dotted">
            which subs are these?
          </span>
        </span>
        {/* 2026-09-02 — the app's own searchable picker (11 call sites, two of
            them already in this module), not a second one. Type to filter,
            arrow keys to move, Enter to choose; it works at 390px.

            2026-10-01, Ben's ruling (item 654): project work goes mainly to the
            floor and special-clean subs, not the nightly one, so three groups
            sit above the list — project subs here, then this building's nightly
            subs, then project subs in the market (8). A sub appears once, in its
            highest group. Then the scoped list, search and "Show all subs" as
            before. The "This service here" panel above still names who did this
            exact service here and what they were paid.

            ⛔ It is scoped, never filtered at the fetch, and a saved sub
            resolves by id whatever list is showing: a line naming a sub outside
            every group — retired, merged, or simply elsewhere — still shows the
            name on first paint and after a reload (TCSD-0163). */}
        <SmartSelect
          entity="service_provider"
          scope={facilityId ? { facility_id: facilityId } : undefined}
          value={line.assigned_partner_id || null}
          onChange={(id) => onChange({ assigned_partner_id: id || '' })}
          isSelectable={isProviderSelectable}
          extraGroups={subGroups}
          entityLabel="sub"
          placeholder="Search subs…"
        />
      </label>

      {/* ⛔ THE PLANNED DATE IS OFF THE FORM — Ben, 2026-09-03. Nobody filled it,
          and asking for two dates to record one visit made the screen longer for
          no answer. ⛔ `service_date_planned` STAYS as a column — history and
          direct database insertion still use it, and `timing_state` is derived
          from the pair — so a row that carries one keeps it and nothing is
          rewritten. It is simply never asked for again. */}
      <label className="mt-3 block">
        <span className="mb-1 block text-[11px] text-tcs-text-secondary">Date done</span>
        <input
          type="date"
          value={line.service_date_actual}
          onChange={(e) => onChange({ service_date_actual: e.target.value })}
          className={INPUT_CLASS}
        />
      </label>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-[11px] text-tcs-text-secondary">Client rate (USD)</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={line.client_rate}
            onChange={(e) => onChange({ client_rate: e.target.value })}
            onBlur={(e) => {
              if (shouldAskAboutZero(e.target.value)) onZeroTyped?.('client', true);
            }}
            className={INPUT_CLASS}
          />
          {/* ⭐ AFB-0085 (1) — "an amount box, OR three taps". The typed-$0 path
              only reaches an AM who already decided to write 0; the one who does
              not know what to put still had an empty box and no way out, which
              is precisely how AFB-0085 describes people inventing a number or
              abandoning the record. Same answers, same consequences. */}
          {onZeroTyped && (
            <button
              type="button"
              onClick={() => onZeroTyped('client', false)}
              className="mt-1 text-[11px] text-tcs-text-secondary underline decoration-dotted underline-offset-2 hover:text-tcs-text"
            >
              Can&rsquo;t give a number?
            </button>
          )}
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] text-tcs-text-secondary">Sub payment (USD)</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={line.sub_rate}
            onChange={(e) => onChange({ sub_rate: e.target.value })}
            onBlur={(e) => {
              if (shouldAskAboutZero(e.target.value)) onZeroTyped?.('sub', true);
            }}
            className={INPUT_CLASS}
          />
          {/* AFB-0085 (2) — the same door on the sub side. "Their hours are on
              their timecard" is a real answer and it is not reachable by
              typing. */}
          {onZeroTyped && (
            <button
              type="button"
              onClick={() => onZeroTyped('sub', false)}
              className="mt-1 text-[11px] text-tcs-text-secondary underline decoration-dotted underline-offset-2 hover:text-tcs-text"
            >
              Can&rsquo;t give a number?
            </button>
          )}
        </label>
      </div>

      {pct !== null && (
        <p
          className={`mt-2 rounded-md px-3 py-2 text-[11.5px] ${
            clears ? 'bg-st-ok-bg text-st-ok-fg' : 'bg-st-warn-bg text-st-warn-fg'
          }`}
        >
          Margin {pct.toFixed(1)}% —{' '}
          {clears
            ? `clears the ${MARGIN_FLOOR_PCT}% floor`
            : `below the ${MARGIN_FLOOR_PCT}% floor. That is a hint, not a block — it still saves.`}
        </p>
      )}
    </div>
  );
}
